import { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  Switch,
  Button,
  IconButton,
} from "@mui/material";
import { X } from "lucide-react";
import { colors } from "./Pipeline.styles";

const ACCENT_COLORS = [
  { name: "Blue", value: "#3B82F6" },
  { name: "Orange", value: "#F97316" },
  { name: "Yellow", value: "#EAB308" },
  { name: "Green", value: "#10B981" },
  { name: "Purple", value: "#8B5CF6" },
  { name: "Dark", value: "#1F2937" },
  { name: "Pink", value: "#EC4899" },
];

const ColumnSettingsModal = ({ open, onClose, columnTitle, currentSettings, onSave }) => {
  const [settings, setSettings] = useState({
    sourceBadge: true,
    serviceIcon: true,
    confidenceMeter: true,
    followUpDate: true,
    quickActions: true,
    accentColor: "#8B5CF6",
  });

  // Update settings when modal opens with current column settings
  useEffect(() => {
    if (open && currentSettings) {
      setSettings(currentSettings);
    } else if (open && !currentSettings) {
      // Reset to defaults when opening for a column without settings
      setSettings({
        sourceBadge: true,
        serviceIcon: true,
        confidenceMeter: true,
        followUpDate: true,
        quickActions: true,
        accentColor: "#8B5CF6",
      });
    }
  }, [open, currentSettings]);

  const handleToggle = (field) => {
    setSettings((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleColorSelect = (color) => {
    setSettings((prev) => ({
      ...prev,
      accentColor: color,
    }));
  };

  const handleSave = () => {
    onSave?.(settings);
    onClose();
  };

  const handleReset = () => {
    setSettings({
      sourceBadge: true,
      serviceIcon: true,
      confidenceMeter: true,
      followUpDate: true,
      quickActions: true,
      accentColor: "#8B5CF6",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 3,
          maxWidth: 360,
          width: "100%",
        },
      }}
    >
      <Box>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: colors.textPrimary,
            }}
          >
            Column Settings
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 28,
              height: 28,
              color: colors.textMuted,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            <X size={18} />
          </IconButton>
        </Stack>

        {/* Card Display Fields */}
        <Box mb={3}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: colors.textMuted,
              letterSpacing: 0.5,
              mb: 2,
            }}
          >
            CARD DISPLAY FIELDS
          </Typography>

          <Stack spacing={1.5}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.textPrimary,
                }}
              >
                Source Badge
              </Typography>
              <Switch
                checked={settings.sourceBadge}
                onChange={() => handleToggle("sourceBadge")}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: colors.primary,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: colors.primary,
                  },
                }}
              />
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.textPrimary,
                }}
              >
                Service Icon
              </Typography>
              <Switch
                checked={settings.serviceIcon}
                onChange={() => handleToggle("serviceIcon")}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: colors.primary,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: colors.primary,
                  },
                }}
              />
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.textPrimary,
                }}
              >
                Confidence Meter
              </Typography>
              <Switch
                checked={settings.confidenceMeter}
                onChange={() => handleToggle("confidenceMeter")}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: colors.primary,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: colors.primary,
                  },
                }}
              />
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.textPrimary,
                }}
              >
                Follow-up Date
              </Typography>
              <Switch
                checked={settings.followUpDate}
                onChange={() => handleToggle("followUpDate")}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: colors.primary,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: colors.primary,
                  },
                }}
              />
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: colors.textPrimary,
                }}
              >
                Quick Actions (Call/WhatsApp)
              </Typography>
              <Switch
                checked={settings.quickActions}
                onChange={() => handleToggle("quickActions")}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: colors.primary,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: colors.primary,
                  },
                }}
              />
            </Stack>
          </Stack>
        </Box>

        {/* Column Accent Color */}
        <Box mb={3}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: colors.textMuted,
              letterSpacing: 0.5,
              mb: 2,
            }}
          >
            COLUMN ACCENT COLOR
          </Typography>

          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {ACCENT_COLORS.map((color) => (
              <Box
                key={color.value}
                onClick={() => handleColorSelect(color.value)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: color.value,
                  cursor: "pointer",
                  border:
                    settings.accentColor === color.value
                      ? `3px solid ${color.value}`
                      : "3px solid transparent",
                  boxShadow:
                    settings.accentColor === color.value
                      ? `0 0 0 2px #FFFFFF, 0 0 0 4px ${color.value}`
                      : "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.1)",
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Action Buttons */}
        <Stack spacing={1.5}>
          <Button
            variant="contained"
            onClick={handleSave}
            fullWidth
            sx={{
              py: 1.5,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              bgcolor: colors.primary,
              color: "#FFFFFF",
              borderRadius: 2.5,
              boxShadow: "none",
              "&:hover": {
                bgcolor: colors.primaryDark,
                boxShadow: "none",
              },
            }}
          >
            Save Changes
          </Button>
          <Button
            variant="text"
            onClick={handleReset}
            fullWidth
            sx={{
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              color: colors.textMuted,
              "&:hover": {
                bgcolor: "transparent",
                color: colors.textSecondary,
              },
            }}
          >
            Reset to Default
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default ColumnSettingsModal;
